import { useCallback } from "react";

import { toast } from "sonner";

import { useSubscriptionAccess } from "../context/SubscriptionAccessContext";
import { emitFeatureGate } from "./surfaces";

function sentenceCase(text) {
  if (!text) {
    return "this action";
  }
  return text.charAt(0).toLowerCase() + text.slice(1);
}

export function getActionAccessMessage(surface, decision) {
  const label = sentenceCase(surface?.label || "this action");

  if (decision?.pending) {
    return `Checking access for ${label}.`;
  }

  if (decision?.reason === "staff_required") {
    return `This action requires staff access.`;
  }

  if (decision?.reason === "feature_unconfigured") {
    return `Access to ${label} is still being configured.`;
  }

  return surface?.deniedMessage || `Your current subscription does not include access to ${label}.`;
}

export function useActionAccess(surface, options = {}) {
  const { getSurfaceDecision } = useSubscriptionAccess();
  const decision = getSurfaceDecision(surface, {
    allowWhileLoading: false,
    ...(options.decisionOptions || {}),
  });

  const ensureAccess = useCallback(
    (runtimeOptions = {}) => {
      if (decision.pending) {
        if (runtimeOptions.notify !== false) {
          toast.info(runtimeOptions.message || getActionAccessMessage(surface, decision));
        }
        return false;
      }

      if (decision.allowed) {
        return true;
      }

      emitFeatureGate(decision);
      if (runtimeOptions.notify !== false) {
        toast.warning(runtimeOptions.message || getActionAccessMessage(surface, decision));
      }
      return false;
    },
    [decision, surface],
  );

  return {
    decision,
    allowed: decision.allowed,
    pending: decision.pending,
    disabled: decision.pending || !decision.allowed,
    blockedMessage: getActionAccessMessage(surface, decision),
    ensureAccess,
  };
}